import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "makerizk_cart_v1";

function loadInitial() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : [];
    } catch (_) {
        return [];
    }
}

export function CartProvider({ children }) {
    const [items, setItems] = useState(loadInitial);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (_) {}
    }, [items]);

    const addItem = useCallback((product, size) => {
        setItems((prev) => {
            const key = `${product.id}__${size || "OS"}`;
            const existing = prev.find((i) => i.key === key);
            if (existing) {
                return prev.map((i) =>
                    i.key === key ? { ...i, quantity: i.quantity + 1 } : i
                );
            }
            return [
                ...prev,
                {
                    key,
                    product_id: product.id,
                    name: product.name,
                    price: product.price,
                    size: size || null,
                    quantity: 1,
                    image: product.images?.[0] || null,
                },
            ];
        });
        setIsOpen(true);
    }, []);

    const removeItem = useCallback((key) => {
        setItems((prev) => prev.filter((i) => i.key !== key));
    }, []);

    const updateQty = useCallback((key, delta) => {
        setItems((prev) =>
            prev
                .map((i) => (i.key === key ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i))
                .filter((i) => i.quantity > 0)
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const value = useMemo(() => {
        const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
        const count = items.reduce((sum, i) => sum + i.quantity, 0);
        return {
            items,
            isOpen,
            setIsOpen,
            addItem,
            removeItem,
            updateQty,
            clear,
            subtotal,
            count,
        };
    }, [items, isOpen, addItem, removeItem, updateQty, clear]);

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
}
